import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowPathIcon,
} from '@heroicons/react/20/solid'
import { cpqApprovalsApi, type CpqApprovalRequestWithDetails } from '../../lib/api'
import { Pagination } from '../../components/ui/Pagination'

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  approved: { bg: 'bg-green-100', text: 'text-green-800' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800' },
  escalated: { bg: 'bg-purple-100', text: 'text-purple-800' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-700' },
}

const ACTION_COLORS: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
  warn: { bg: 'bg-amber-50', text: 'text-amber-700', icon: ExclamationTriangleIcon },
  require_approval: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: ClockIcon },
  block: { bg: 'bg-red-50', text: 'text-red-700', icon: XMarkIcon },
}

export function ApprovalsQueuePage() {
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [selectedRequest, setSelectedRequest] = useState<CpqApprovalRequestWithDetails | null>(null)
  const [showDecisionModal, setShowDecisionModal] = useState(false)
  const [decisionType, setDecisionType] = useState<'approve' | 'reject'>('approve')
  const [decisionNotes, setDecisionNotes] = useState('')
  const queryClient = useQueryClient()

  const filters: Record<string, string> = {}
  if (statusFilter !== 'all') filters.status = statusFilter

  const { data: response, isLoading } = useQuery({
    queryKey: ['cpq-approvals', { page, limit, filters }],
    queryFn: () => cpqApprovalsApi.list({ page, limit, filters: Object.keys(filters).length > 0 ? filters : undefined }),
  })

  const { data: stats } = useQuery({
    queryKey: ['cpq-approvals', 'stats'],
    queryFn: () => cpqApprovalsApi.getStats(),
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) => cpqApprovalsApi.approve(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cpq-approvals'] })
      setShowDecisionModal(false)
      setSelectedRequest(null)
      setDecisionNotes('')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => cpqApprovalsApi.reject(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cpq-approvals'] })
      setShowDecisionModal(false)
      setSelectedRequest(null)
      setDecisionNotes('')
    },
  })

  const requests = response?.data || []

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const openDecisionModal = (request: CpqApprovalRequestWithDetails, type: 'approve' | 'reject') => {
    setSelectedRequest(request)
    setDecisionType(type)
    setDecisionNotes('')
    setShowDecisionModal(true)
  }

  const handleDecision = () => {
    if (!selectedRequest) return

    if (decisionType === 'approve') {
      approveMutation.mutate({ id: selectedRequest.id, notes: decisionNotes || undefined })
    } else {
      if (!decisionNotes.trim()) {
        alert('Please provide a reason for rejection')
        return
      }
      rejectMutation.mutate({ id: selectedRequest.id, notes: decisionNotes })
    }
  }

  const formatPrice = (price: string | null | undefined, currency: string = 'EUR') => {
    if (!price) return '-'
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(parseFloat(price))
  }

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatUserName = (user?: { firstName: string | null; lastName: string | null; email: string }) => {
    if (!user) return '-'
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.email
  }

  if (isLoading) return <div className="p-8">Loading...</div>

  return (
    <>
      {/* Secondary column - Filters and Stats */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Approval Queue</h2>
            <p className="mt-1 text-sm text-gray-500">
              Review and process pricing approval requests
            </p>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Status</h3>
            <div className="flex flex-wrap gap-2">
              {['all', 'pending', 'approved', 'rejected', 'escalated'].map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Today's Activity</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Pending</dt>
                <dd className="font-medium text-yellow-600">{stats?.pending || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Approved Today</dt>
                <dd className="font-medium text-green-600">{stats?.approvedToday || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Rejected Today</dt>
                <dd className="font-medium text-red-600">{stats?.rejectedToday || 0}</dd>
              </div>
            </dl>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Links</h3>
            <div className="space-y-2">
              <Link
                to="/quotes"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <DocumentTextIcon className="h-4 w-4" />
                View All Quotes
              </Link>
              <Link
                to="/settings/cpq-rules"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Manage CPQ Rules
              </Link>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Approval Queue</h1>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Request</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Quote</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Violation</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Requested By</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <div className="font-medium text-gray-900">{request.requestNumber}</div>
                      <div className="text-gray-500 text-xs">{formatDate(request.createdAt)}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link to={`/quotes/${request.quoteId}`} className="hover:text-brand-600">
                        <div className="font-medium text-gray-900">{request.quoteNumber || '-'}</div>
                        <div className="text-gray-500 text-xs">{formatPrice(request.quoteDocTotal)}</div>
                      </Link>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="font-medium text-gray-900">{request.ruleName || '-'}</div>
                      <div className="text-gray-500 text-xs max-w-xs truncate">
                        {request.violationDetails.message}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatUserName(request.requestedBy as any)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[request.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[request.status]?.text || 'text-gray-700'}`}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => openDecisionModal(request, 'approve')}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                          >
                            <CheckIcon className="h-3 w-3" />
                            Approve
                          </button>
                          <button
                            onClick={() => openDecisionModal(request, 'reject')}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                          >
                            <XMarkIcon className="h-3 w-3" />
                            Reject
                          </button>
                        </div>
                      )}
                      {request.status !== 'pending' && (
                        <span className="text-gray-400 text-xs">
                          {request.decisionNotes ? 'View details' : 'No action needed'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                      No approval requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Decision Modal */}
      {showDecisionModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDecisionModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${decisionType === 'approve' ? 'bg-green-100' : 'bg-red-100'}`}>
                  {decisionType === 'approve' ? (
                    <CheckIcon className="h-6 w-6 text-green-600" />
                  ) : (
                    <XMarkIcon className="h-6 w-6 text-red-600" />
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900">
                    {decisionType === 'approve' ? 'Approve Request' : 'Reject Request'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {selectedRequest.requestNumber} - {selectedRequest.ruleName}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {selectedRequest.violationDetails.message}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  {decisionType === 'approve' ? 'Notes (optional)' : 'Reason for rejection (required)'}
                </label>
                <textarea
                  rows={3}
                  value={decisionNotes}
                  onChange={(e) => setDecisionNotes(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  placeholder={decisionType === 'approve' ? 'Add any notes...' : 'Explain why this request is being rejected...'}
                />
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleDecision}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:col-start-2 ${
                    decisionType === 'approve'
                      ? 'bg-green-600 hover:bg-green-500'
                      : 'bg-red-600 hover:bg-red-500'
                  } disabled:opacity-50`}
                >
                  {(approveMutation.isPending || rejectMutation.isPending) ? 'Processing...' : decisionType === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDecisionModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
